import type {
  SupabaseClient,
} from '@supabase/supabase-js';

import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  planCanonicalTreeEndpointFacts,
} from './CanonicalTreeEndpointFactAuthoringPlanner';

import {
  createCanonicalTreeFactPlanOperationId,
  prepareCanonicalTreeFactPlanRuntimeEnvelope,
  type CanonicalTreeFactPlanRuntimeCreateSubjectProfileV2,
} from './CanonicalTreeFactPlanRuntimeAdapter';

export const
CANONICAL_GENERIC_INVITE_ACCEPTANCE_RPC =
  'accept_generic_invite_canonical_atomic' as const;

export type CanonicalGenericInviteRelationshipIntent =
  | 'mother'
  | 'father'
  | 'child'
  | 'sibling'
  | 'spouse'
  | 'cousin'
  | 'aunt-uncle'
  | 'niece-nephew'
  | 'grandparent'
  | 'grandchild'
  | 'other';

export interface CanonicalGenericInviteAcceptanceInput {
  readonly supabase:
    SupabaseClient;

  readonly members:
    readonly GraphMember[];

  readonly inviterUserId:
    string;

  readonly claimantUserId:
    string;

  readonly inviteToken:
    string;

  readonly acceptanceMode:
    string | null;

  readonly relationshipIntent:
    CanonicalGenericInviteRelationshipIntent;

  readonly createNewName:
    string;

  readonly similarityWarningMemberIds?:
    readonly string[];

  readonly operationId:
    string;
}

export interface CanonicalGenericInviteBindingCertificate {
  readonly operationId:
    string;

  readonly subjectMemberId:
    string;

  readonly claimantUserId:
    string;

  readonly ownerUserId:
    string;

  readonly viewerMemberId:
    string;

  readonly eAuditId:
    string;
}

export interface CanonicalGenericInviteAcceptanceSuccess {
  readonly ok: true;

  readonly status:
    | 'accepted'
    | 'already_applied';

  readonly operationId:
    string;

  readonly subjectMemberId:
    string;

  readonly inviteId:
    string;

  readonly seatClaimed:
    boolean;

  readonly seatReturned:
    boolean;

  readonly idempotent:
    boolean;

  readonly bindingCertificate:
    CanonicalGenericInviteBindingCertificate;

  readonly rpcCount: 1;
}

export interface CanonicalGenericInviteAcceptanceFailure {
  readonly ok: false;

  readonly status:
    | 'blocked'
    | 'rpc-error'
    | 'invalid-certificate';

  readonly code:
    string;

  readonly message:
    string;

  readonly rpcCount:
    0 | 1;
}

export type CanonicalGenericInviteAcceptanceResult =
  | CanonicalGenericInviteAcceptanceSuccess
  | CanonicalGenericInviteAcceptanceFailure;

const blocked = (
  code: string,
  message: string,
): CanonicalGenericInviteAcceptanceFailure =>
  Object.freeze({
    ok:
      false as const,

    status:
      'blocked' as const,

    code,
    message,

    rpcCount:
      0 as const,
  });

const rpcFailure = (
  code: string,
  message: string,
): CanonicalGenericInviteAcceptanceFailure =>
  Object.freeze({
    ok:
      false as const,

    status:
      'rpc-error' as const,

    code,
    message,

    rpcCount:
      1 as const,
  });

const invalidCertificate = (
  code: string,
  message: string,
): CanonicalGenericInviteAcceptanceFailure =>
  Object.freeze({
    ok:
      false as const,

    status:
      'invalid-certificate' as const,

    code,
    message,

    rpcCount:
      1 as const,
  });

const isRecord = (
  value: unknown,
): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value);

const activeMember = (
  member: GraphMember,
): boolean =>
  member.approvalStatus !==
    'rejected' &&
  (
    member.profileState as string
  ) !==
    'deleted';

const normalizeAcceptanceMode = (
  value: string | null,
): string | null => {
  const normalized =
    (
      value ??
      ''
    )
      .trim()
      .toUpperCase();

  if (!normalized) {
    return 'FAMILY_PROFILE';
  }

  if (
    normalized ===
      'FAMILY_PROFILE' ||
    normalized ===
      'SPONSORED_SEAT' ||
    normalized ===
      'SELF_PAID' ||
    normalized ===
      'FAMILY_CONTRIBUTOR'
  ) {
    return normalized;
  }

  return null;
};

const createProfileForIntent = (
  intent:
    CanonicalGenericInviteRelationshipIntent,
  name:
    string,
):
  CanonicalTreeFactPlanRuntimeCreateSubjectProfileV2 |
  null => {
  const common = {
    name,

    photoUrl:
      null,

    birthYear:
      null,

    birthDate:
      null,

    birthMonth:
      null,

    birthDay:
      null,

    livingStatus:
      null,

    notes:
      null,

    city:
      null,

    description:
      null,

    approvalStatus:
      'approved' as const,

    relationshipOverride:
      null,

    relationshipWorkflow:
      null,
  };

  switch (intent) {
    case 'mother':
      return Object.freeze({
        ...common,
        relationship:
          'mother' as const,
        side:
          'mother' as const,
      });

    case 'father':
      return Object.freeze({
        ...common,
        relationship:
          'father' as const,
        side:
          'father' as const,
      });

    case 'child':
      return Object.freeze({
        ...common,
        relationship:
          'child' as const,
        side:
          'self' as const,
      });

    case 'sibling':
      return Object.freeze({
        ...common,
        relationship:
          'sibling' as const,
        side:
          'both' as const,
      });

    case 'spouse':
      return Object.freeze({
        ...common,
        relationship:
          'spouse' as const,
        side:
          'self' as const,
      });

    default:
      return null;
  }
};

export const
createCanonicalGenericInviteAcceptanceOperationId =
  (): string =>
    createCanonicalTreeFactPlanOperationId();

export async function
executeCanonicalGenericInviteAcceptanceRuntime(
  input:
    CanonicalGenericInviteAcceptanceInput,
): Promise<
  CanonicalGenericInviteAcceptanceResult
> {
  const inviterUserId =
    input.inviterUserId.trim();

  const claimantUserId =
    input.claimantUserId.trim();

  const inviteToken =
    input.inviteToken.trim();

  const createNewName =
    input.createNewName.trim();

  if (
    !inviterUserId ||
    !claimantUserId
  ) {
    return blocked(
      'GENERIC_INVITE_PRINCIPAL_REQUIRED',
      'Generic invite acceptance requires exact inviter and claimant principals.',
    );
  }

  if (
    inviterUserId ===
      claimantUserId
  ) {
    return blocked(
      'INVITER_CANNOT_ACCEPT_OWN_INVITE',
      'The inviter cannot accept their own generic invitation.',
    );
  }

  if (
    inviteToken.length <
      16
  ) {
    return blocked(
      'INVALID_INVITE_TOKEN',
      'Generic invite acceptance requires the complete secure invitation token.',
    );
  }

  const acceptanceMode =
    normalizeAcceptanceMode(
      input.acceptanceMode,
    );

  if (!acceptanceMode) {
    return blocked(
      'GENERIC_ACCEPTANCE_MODE_INVALID',
      'Generic invite acceptance mode is not recognized.',
    );
  }

  if (!createNewName) {
    return blocked(
      'CREATE_NEW_SUBJECT_NAME_REQUIRED',
      'Generic invite create-new identity requires a non-empty canonical name.',
    );
  }

  if (
    input.members.length ===
      0
  ) {
    return blocked(
      'GENERIC_INVITER_GRAPH_REQUIRED',
      'The exact inviter canonical graph must be loaded before generic acceptance.',
    );
  }

  if (
    input.members.some(
      member =>
        member.ownerUserId !==
          inviterUserId,
    )
  ) {
    return blocked(
      'GENERIC_CROSS_GRAPH_SNAPSHOT_BLOCKED',
      'Generic invite canonical planning requires one exact inviter-owned graph snapshot.',
    );
  }

  const inviterViewerCandidates =
    input.members.filter(
      member =>
        activeMember(member) &&
        member.ownerUserId ===
          inviterUserId &&
        member.claimedByUserId ===
          inviterUserId,
    );

  if (
    inviterViewerCandidates.length !==
      1
  ) {
    return blocked(
      'GENERIC_EXACT_CANONICAL_VIEWER_REQUIRED',
      'Generic invite acceptance requires exactly one active canonical inviter viewer.',
    );
  }

  const similarityWarnings =
    Array.from(
      new Set(
        (
          input
            .similarityWarningMemberIds ??
          []
        )
          .map(
            value =>
              value.trim(),
          )
          .filter(Boolean),
      ),
    ).sort();

  if (
    similarityWarnings.length >
      0
  ) {
    return blocked(
      'GENERIC_EXISTING_PROFILE_REQUIRES_TARGETED_INVITE',
      'A possible existing placecard requires a direct person-specific invitation before generic create-new acceptance can continue.',
    );
  }

  const profile =
    createProfileForIntent(
      input.relationshipIntent,
      createNewName,
    );

  if (!profile) {
    return blocked(
      'GENERIC_RELATIONSHIP_INTENT_NOT_EXECUTABLE',
      'This generic relationship intent does not yet have sufficient canonical endpoint evidence for create-new execution.',
    );
  }

  const viewer =
    inviterViewerCandidates[0];

  const plannerResult =
    planCanonicalTreeEndpointFacts({
      members:
        input.members,

      viewerMemberId:
        viewer.id,

      relationship:
        profile.relationship,

      optionKey:
        input.relationshipIntent,

      requestedSide:
        profile.side,

      subjectMemberId:
        null,

      connectorMemberId:
        input.relationshipIntent ===
          'spouse'
          ? viewer.id
          : null,

      tappedMemberId:
        null,

      secondaryConnectorMemberId:
        null,

      displayLabel:
        null,

      relationshipOverride:
        null,
    });

  if (
    plannerResult.status !==
      'READY_FOR_IDENTITY_RESOLUTION' ||
    plannerResult.factProposals.length ===
      0 ||
    plannerResult.subjectIdentityResolutionRequired !==
      true
  ) {
    return blocked(
      'GENERIC_B_PLAN_NOT_READY',
      [
        'Canonical B did not authorize generic create-new execution.',
        ...plannerResult.blockers,
        ...plannerResult.requiredEvidence,
      ].join(' '),
    );
  }

  const prepared =
    prepareCanonicalTreeFactPlanRuntimeEnvelope({
      supabase:
        input.supabase,

      members:
        input.members,

      viewerMemberId:
        viewer.id,

      plannerResult,

      selectedExistingMemberId:
        null,

      createNewExplicit:
        true,

      similarityWarningMemberIds:
        [],

      operationId:
        input.operationId,

      createNewSubjectName:
        createNewName,

      createNewSubjectProfileV2:
        profile,

      requireFullSubjectMember:
        true,
    });

  if (prepared.ok === false) {
    return prepared;
  }

  if (
    prepared.identityMode !==
      'CREATE_NEW_EXPLICIT' ||
    prepared.rpcArgs
      .p_canonical_subject_member_id !==
      null ||
    prepared.createNewSubjectVersion !==
      2
  ) {
    return blocked(
      'GENERIC_CREATE_NEW_ENVELOPE_REQUIRED',
      'Generic invite execution may use only the canonical explicit-create v2 envelope.',
    );
  }

  try {
    const {
      data,
      error,
    } =
      await input.supabase.rpc(
        CANONICAL_GENERIC_INVITE_ACCEPTANCE_RPC,
        {
          p_invite_token:
            inviteToken,

          p_acceptance_mode:
            acceptanceMode,

          ...prepared.rpcArgs,
        },
      );

    if (error) {
      return rpcFailure(
        'GENERIC_ACCEPTANCE_RPC_REJECTED',
        error.message ||
          'Canonical generic invite acceptance RPC rejected the operation.',
      );
    }

    if (
      !isRecord(data)
    ) {
      return invalidCertificate(
        'GENERIC_ACCEPTANCE_CERTIFICATE_OBJECT_REQUIRED',
        'Canonical generic invite acceptance returned no durable certificate object.',
      );
    }

    if (
      data.ok !==
        true ||
      (
        data.status !==
          'accepted' &&
        data.status !==
          'already_applied'
      )
    ) {
      return invalidCertificate(
        'GENERIC_ACCEPTANCE_STATUS_INVALID',
        'Canonical generic invite acceptance returned a non-success status.',
      );
    }

    if (
      data.operationId !==
        input.operationId
    ) {
      return invalidCertificate(
        'GENERIC_OPERATION_ID_MISMATCH',
        'Canonical generic acceptance did not preserve the caller operation id.',
      );
    }

    const subjectMemberId =
      typeof data.subjectMemberId ===
        'string'
        ? data.subjectMemberId
        : '';

    if (!subjectMemberId) {
      return invalidCertificate(
        'GENERIC_SUBJECT_MEMBER_REQUIRED',
        'Canonical generic acceptance did not certify the canonical subject member.',
      );
    }

    const inviteId =
      typeof data.inviteId ===
        'string'
        ? data.inviteId
        : typeof data.inviteId ===
            'number'
          ? String(
              data.inviteId,
            )
          : '';

    if (!inviteId) {
      return invalidCertificate(
        'GENERIC_INVITE_ID_REQUIRED',
        'Canonical generic acceptance did not certify the consumed invitation.',
      );
    }

    if (
      !isRecord(
        data.bindingCertificate,
      )
    ) {
      return invalidCertificate(
        'GENERIC_BINDING_CERTIFICATE_REQUIRED',
        'Canonical generic acceptance did not return its durable binding certificate.',
      );
    }

    const certificate =
      data.bindingCertificate;

    if (
      certificate.operationId !==
        input.operationId ||
      certificate.subjectMemberId !==
        subjectMemberId ||
      certificate.claimantUserId !==
        claimantUserId ||
      certificate.ownerUserId !==
        inviterUserId ||
      certificate.viewerMemberId !==
        viewer.id ||
      certificate.eAuditId !==
        input.operationId
    ) {
      return invalidCertificate(
        'GENERIC_BINDING_CERTIFICATE_MISMATCH',
        'Canonical generic binding certificate does not match the exact invite transaction.',
      );
    }

    if (
      typeof data.seatClaimed !==
        'boolean' ||
      typeof data.seatReturned !==
        'boolean' ||
      typeof data.idempotent !==
        'boolean'
    ) {
      return invalidCertificate(
        'GENERIC_LIFECYCLE_CERTIFICATE_INVALID',
        'Canonical generic acceptance did not certify seat and idempotency state.',
      );
    }

    return Object.freeze({
      ok:
        true as const,

      status:
        data.status,

      operationId:
        input.operationId,

      subjectMemberId,

      inviteId,

      seatClaimed:
        data.seatClaimed,

      seatReturned:
        data.seatReturned,

      idempotent:
        data.idempotent,

      bindingCertificate:
        Object.freeze({
          operationId:
            input.operationId,

          subjectMemberId,

          claimantUserId,

          ownerUserId:
            inviterUserId,

          viewerMemberId:
            viewer.id,

          eAuditId:
            input.operationId,
        }),

      rpcCount:
        1 as const,
    });
  } catch (error) {
    return rpcFailure(
      'GENERIC_ACCEPTANCE_RPC_UNEXPECTED_ERROR',
      error instanceof Error
        ? error.message
        : 'Unexpected canonical generic invite acceptance failure.',
    );
  }
}
